import { useEffect } from 'react';

import useDataStore from '../stores/useDataStore';
import useUserStore from '../stores/useUserStore';
import {
  applyBrandPaletteToRoot,
  DEFAULT_BRAND_THEME,
  resolveBrandPalette,
} from '../utils/speciesTheme';
import useBrand from './useBrand';

/**
 * Keeps the global `--brand-*` CSS variables in sync with the currently
 * selected species and dark/light mode. Mount this once near the root
 * of the app (e.g. inside <App/>). Subsequent calls are cheap.
 *
 * Effect:
 * - Reads `selectedSpecies` from useDataStore.
 * - Reads `darkMode` from useUserStore.
 * - Writes --brand-50..900 (hex) and --brand-*-rgb triplets on
 *   document.documentElement every time either changes.
 */
export default function useSpeciesTheme(): void {
  const selectedSpecies = useDataStore((state) => state.selectedSpecies);
  const darkMode = useUserStore((state) => state.darkMode);
  const { key: brandKey } = useBrand();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const mode = darkMode ? 'dark' : 'light';
    const palette = resolveBrandPalette(selectedSpecies, mode, brandKey);
    applyBrandPaletteToRoot(palette);
  }, [selectedSpecies, darkMode, brandKey]);
}

/**
 * Apply the default (cherry) palette immediately, without waiting for
 * React to mount. Useful to call from an app entry point so SSR-like
 * first paint already has the variables defined.
 */
export function primeDefaultBrandPalette(darkMode: boolean): void {
  if (typeof document === 'undefined') return;
  const palette = darkMode ? DEFAULT_BRAND_THEME.dark : DEFAULT_BRAND_THEME.light;
  applyBrandPaletteToRoot(palette);
}
